/**
 * Agent wire contracts — cross-boundary shapes shared by backend-tms,
 * backend-wms, web-tms, mobile-wms, and `@axon/agent-core`.
 *
 * Timestamp policy: these are post-JSON wire shapes, so `expiresAt` is
 * always a `string` (ISO 8601). Backend emitters and agent-core runtime
 * types use native `Date`; the JSON boundary normalizes.
 *
 * @module AgentWireContracts
 */

// ---------------------------------------------------------------------------
// Enums & values
// ---------------------------------------------------------------------------

export type AgentRunStatus =
  | 'pending'
  | 'running'
  | 'awaiting_confirm'
  | 'completed'
  | 'failed'
  | 'cancelled';

export const AGENT_RUN_STATUSES = [
  'pending',
  'running',
  'awaiting_confirm',
  'completed',
  'failed',
  'cancelled',
] as const satisfies readonly AgentRunStatus[];

export type AgentTerminationReason =
  | 'final_answer'
  | 'iteration_cap'
  | 'budget'
  | 'tool_error_streak'
  | 'cancelled'
  | 'cycle_blocked'
  | 'awaiting_confirm'
  | 'llm_exhausted'
  | 'internal_error';

export const AGENT_TERMINATION_REASONS = [
  'final_answer',
  'iteration_cap',
  'budget',
  'tool_error_streak',
  'cancelled',
  'cycle_blocked',
  'awaiting_confirm',
  'llm_exhausted',
  'internal_error',
] as const satisfies readonly AgentTerminationReason[];

export type ToolSafetyLevel = 'safe' | 'moderate' | 'destructive';

export const TOOL_SAFETY_LEVELS = [
  'safe',
  'moderate',
  'destructive',
] as const satisfies readonly ToolSafetyLevel[];

// ---------------------------------------------------------------------------
// REST response contracts
// ---------------------------------------------------------------------------

export interface PendingConfirmation {
  readonly confirmationId: string;
  /** ISO-8601 timestamp — Date on the backend, string after JSON serialization. */
  readonly expiresAt: string;
  readonly toolName: string;
  readonly safetyLevel: ToolSafetyLevel;
}

/** Response item for `GET /agents`. */
export interface AgentSummary {
  readonly name: string;
  readonly description: string;
  readonly tools: readonly string[];
}

/** Response for `POST /agents/:name/run` — lean summary of an agent run. */
export interface AgentRunSummary {
  readonly runId: string;
  readonly agentName: string;
  readonly status: AgentRunStatus;
  readonly terminationReason: AgentTerminationReason;
  readonly costUsd: number;
  readonly iterations: number;
  readonly durationMs: number;
  readonly finalAnswer?: string;
  /** Populated iff `status === 'awaiting_confirm'`. */
  readonly pendingConfirmation?: PendingConfirmation;
}

/**
 * Projection returned by `GET /agents/runs/:id`, `GET /agents/runs`, and
 * `POST /agents/runs/:id/confirm`. Money fields are strings to preserve
 * Postgres `numeric` precision across the wire.
 */
export interface AgentRunDetail {
  readonly id: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly agentName: string;
  readonly parentRunId?: string;
  readonly status: AgentRunStatus;
  readonly terminationReason?: AgentTerminationReason;
  readonly goalPrompt: string;
  readonly finalAnswer?: string;
  /** Numeric as string — preserves Postgres precision. */
  readonly budgetUsdCeiling: string;
  /** Numeric as string — preserves Postgres precision. */
  readonly costUsdSoFar: string;
  readonly iterations: number;
  readonly durationMs?: number;
  readonly conversationId?: string;
  /** ISO-8601 timestamp. */
  readonly createdAt: string;
  /** ISO-8601 timestamp. */
  readonly endedAt?: string;
  readonly pendingConfirmationId?: string;
  /** ISO-8601 timestamp. */
  readonly pendingConfirmationExpiresAt?: string;
  /**
   * Full pending-confirmation payload (toolName + safetyLevel + id +
   * expiry), populated iff `status === 'awaiting_confirm'`. Mirrors the
   * `AgentRunSummary.pendingConfirmation` shape so a polled detail can
   * drive the Approve/Decline card without a live WebSocket event.
   */
  readonly pendingConfirmation?: PendingConfirmation;
  /**
   * Pointer to the continuation run spawned when a destructive-tool
   * confirmation resolved successfully. Set on the parent; the child's
   * `parentRunId` points back. Null on first-confirmation runs that were
   * never resumed, declined, or whose confirmed tool failed.
   */
  readonly continuationRunId?: string;
}

/** Step kinds recorded in the append-only `agent_steps` audit log. */
export type AgentStepType = 'llm_call' | 'tool_call' | 'tool_result' | 'handoff' | 'error';

export const AGENT_STEP_TYPES = [
  'llm_call',
  'tool_call',
  'tool_result',
  'handoff',
  'error',
] as const satisfies readonly AgentStepType[];

/**
 * Response item for `GET /agents/runs/:id/steps` — the ordered step log
 * projected for inspection (mirrors backend `AgentStepDto`). Deliberately
 * carries no `input`/`output` payloads: those are PII-adjacent and stay
 * server-side; the UI gets the metadata needed for per-step timing/cost
 * display.
 */
export interface AgentStepSummary {
  readonly id: string;
  /** Monotonic position within the run (0-based). */
  readonly stepIndex: number;
  readonly stepType: AgentStepType;
  /** Tool name for `tool_call` / `tool_result` steps. */
  readonly toolName?: string;
  /** LLM provider that served an `llm_call` step. */
  readonly provider?: string;
  /** Effective model id (post-tier resolution). */
  readonly model?: string;
  readonly inputTokens?: number;
  readonly outputTokens?: number;
  /** Numeric as string — preserves Postgres precision. */
  readonly costUsd?: string;
  readonly durationMs?: number;
  /** Short error marker for `error` steps. */
  readonly errorCode?: string;
  /** ISO-8601 timestamp. */
  readonly createdAt: string;
}

// ---------------------------------------------------------------------------
// WebSocket contracts — `/agents` namespace
// ---------------------------------------------------------------------------

/**
 * Wire event names emitted on the `/agents` WebSocket namespace. Six are
 * relayed one-to-one from the internal `AGENT_RUN_EVENTS` + `AGENT_TOOL_EVENTS`
 * buses; `ERROR` is gateway-initiated only (auth / validation).
 *
 * Kept as runtime constants so gateway registrations and client listeners
 * share the same source — no hand-copied literal strings to drift.
 */
export const AGENT_WS_EVENTS = {
  RUN_STARTED: 'agent:run_started',
  RUN_COMPLETED: 'agent:run_completed',
  TOOL_EXECUTED: 'agent:tool_executed',
  TOOL_FAILED: 'agent:tool_failed',
  AWAITING_CONFIRM: 'agent:awaiting_confirm',
  CONFIRMED: 'agent:confirmed',
  HANDOFF_DISPATCHED: 'agent:handoff_dispatched',
  HANDOFF_RETURNED: 'agent:handoff_returned',
  /**
   * Fires once per ReAct iteration just before the planner calls the
   * LLM. Lets the UI render "AI is reasoning…" / "Iteration N of M"
   * progress instead of a static spinner during long runs.
   */
  LLM_ITERATION_STARTED: 'agent:llm_iteration_started',
  /**
   * Fires whenever the streaming bridge extracts new final-answer
   * tokens from the LLM stream. Action-emitting iterations stay silent
   * — JSON tool-call output is not user-visible. Frontend buffers these
   * into a single live-typing timeline row.
   */
  LLM_CHUNK: 'agent:llm_chunk',
  ERROR: 'agent:error',
} as const;

export type AgentWsEventName = (typeof AGENT_WS_EVENTS)[keyof typeof AGENT_WS_EVENTS];

/** Relay events that the gateway forwards from the internal bus (excludes `ERROR`). */
export const AGENT_WS_RELAY_EVENTS = [
  AGENT_WS_EVENTS.RUN_STARTED,
  AGENT_WS_EVENTS.RUN_COMPLETED,
  AGENT_WS_EVENTS.TOOL_EXECUTED,
  AGENT_WS_EVENTS.TOOL_FAILED,
  AGENT_WS_EVENTS.AWAITING_CONFIRM,
  AGENT_WS_EVENTS.CONFIRMED,
  AGENT_WS_EVENTS.HANDOFF_DISPATCHED,
  AGENT_WS_EVENTS.HANDOFF_RETURNED,
  AGENT_WS_EVENTS.LLM_ITERATION_STARTED,
  AGENT_WS_EVENTS.LLM_CHUNK,
] as const;

/**
 * Coalesce LLM token emits into ~50ms batches before crossing the
 * WebSocket boundary. Avoids a 200+ events-per-second storm on long
 * answers without hurting the live-typing UX feel — chatbot streaming
 * uses a similar window.
 *
 * Defined here (not in backend constants) so the frontend's debounce
 * window matches the backend's batch window from one source.
 */
export const AGENT_STREAM_CHUNK_BATCH_MS = 50;

// AGENT_WS_NAMESPACE + AGENT_WS_ROOM_SCOPE moved to `./ws-namespaces.ts`
// (single source of truth for all WS namespace strings). Re-exported
// from `@axon/common` via the same barrel — consumers don't change.

/** Wire payload for `RUN_STARTED` + `RUN_COMPLETED` (post-JSON). */
export interface AgentRunLifecycleEvent {
  readonly runId: string;
  readonly agentName: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly status?: AgentRunStatus;
  readonly terminationReason?: AgentTerminationReason;
  readonly durationMs?: number;
  readonly costUsd?: number;
  readonly iterations?: number;
  /**
   * Pointer to a continuation run spawned after a destructive-tool
   * confirmation resolved successfully. Only populated on `RUN_COMPLETED`
   * for the parent run; absent on the child's `RUN_COMPLETED` (its
   * `parentRunId` covers the inverse direction). Lets WS subscribers
   * (other tabs, mobile clients, future live-timeline views) follow the
   * chain in real-time without polling `GET /agents/runs/:id`.
   */
  readonly continuationRunId?: string;
}

/** Wire payload for `TOOL_EXECUTED` (post-JSON). */
export interface AgentToolExecutedEvent {
  readonly runId: string;
  readonly toolName: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly durationMs: number;
  readonly safetyLevel: ToolSafetyLevel;
}

/** Wire payload for `TOOL_FAILED` (post-JSON). */
export interface AgentToolFailedEvent {
  readonly runId: string;
  readonly toolName: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly error: string;
}

/** Wire payload for `AWAITING_CONFIRM` (post-JSON). */
export interface AgentAwaitingConfirmEvent {
  readonly runId?: string;
  readonly confirmationId: string;
  /** ISO-8601 timestamp. */
  readonly expiresAt: string;
  readonly toolName: string;
  readonly safetyLevel: ToolSafetyLevel;
  readonly tenantId: string;
  readonly userId: string;
}

/** Wire payload for `CONFIRMED` (post-JSON). */
export interface AgentConfirmedEvent {
  readonly confirmationId: string;
  readonly confirmed: boolean;
  readonly toolName: string;
  readonly tenantId: string;
  readonly userId: string;
}

/**
 * Wire payload for `HANDOFF_DISPATCHED` — emitted when a parent
 * specialist invokes the built-in `__handoff` tool. The UI uses this to
 * render a "→ ChildAgent (handoff: <reason>)" timeline row.
 */
export interface AgentHandoffDispatchedEvent {
  readonly tenantId: string;
  readonly userId: string;
  readonly fromAgent: string;
  readonly toAgent: string;
  readonly parentRunId: string;
  readonly reason: string;
}

/**
 * Wire payload for `HANDOFF_RETURNED` — emitted when the child run
 * completes (any termination). Carries the `outcome` so the UI can
 * distinguish a successful delegation from a cycle-blocked or failed
 * one without needing to re-correlate against `RUN_COMPLETED`.
 */
export interface AgentHandoffReturnedEvent {
  readonly tenantId: string;
  readonly userId: string;
  readonly fromAgent: string;
  readonly toAgent: string;
  readonly parentRunId: string;
  readonly childRunId: string;
  readonly outcome: 'success' | 'failed' | 'cycle_blocked';
  readonly costUsd: number;
  readonly durationMs: number;
}

/**
 * Wire payload for `LLM_ITERATION_STARTED`. Mirrors the iteration-counter
 * semantics in `AgentRunLifecycleEvent.iterations`: incremented monotonically
 * per ReAct iteration before the LLM call begins. Lets the UI render a
 * "Iteration N" progress indicator without polling.
 */
export interface AgentLlmIterationStartedEvent {
  readonly runId: string;
  readonly agentName: string;
  readonly iteration: number;
  readonly tenantId: string;
  readonly userId: string;
}

/**
 * Wire payload for `LLM_CHUNK`. `content` is the substring of new
 * final-answer text since the last chunk — frontend appends to a single
 * live-typing timeline row. Action-emitting iterations never produce
 * `LLM_CHUNK` events (the bridge filters them via the partial-JSON
 * extractor).
 */
export interface AgentLlmChunkEvent {
  readonly runId: string;
  readonly agentName: string;
  readonly iteration: number;
  readonly content: string;
  readonly tenantId: string;
  readonly userId: string;
}

/**
 * Wire payload for `ERROR` (post-JSON). Gateway-initiated only — sent on
 * auth failure, malformed payloads, or rate-limit rejection. Never relayed
 * from the internal bus.
 */
export interface AgentErrorEvent {
  readonly code: string;
  readonly message: string;
}

/** Audit-trail loss alarm payload — internal bus event, not relayed to WS clients. */
export interface AgentStepPersistFailedPayload {
  readonly runId: string;
  readonly tenantId: string;
  readonly toolName?: string;
  readonly stepType: string;
  readonly error: string;
}
